import { type ReactNode, type RefObject } from 'react';

import { DateTimePicker } from '@/components';
import AddButton from '@/components/Button/AddButton';
import { Input } from '@/components/Inputs';

import { AiGenerating } from '../AiGenerating';
import type { AiChatMessage, ItineraryItem, ItinerarySuggestion } from '../eventWizard.types';
import { fmtRange, fromIsoValue, toIsoValue } from '../eventWizard.utils';
import s from '../EventWizardModal.module.scss';

import { ItineraryAiPanel } from './ItineraryAiPanel';
import { ItineraryDetailsModal } from './ItineraryDetailsModal';

interface ItineraryTabProps {
  planTabs: ReactNode;
  eventStart: Date | null;
  eventEnd: Date | null;
  itinerary: ItineraryItem[];
  onRemoveItem: (id: number) => void;
  onAddItem: () => void;
  itineraryMode: string;
  aiLoading: boolean;

  itDate: Date | null;
  itEndDate: Date | null;
  onItDatesChange: (start: Date | null, end: Date | null) => void;
  itActivity: string;
  onItActivityChange: (value: string) => void;

  showDetails: boolean;
  onOpenDetails: () => void;
  onCloseDetails: () => void;
  detailActivity: string;
  onDetailActivityChange: (value: string) => void;
  detailDate: Date | null;
  detailEndDate: Date | null;
  onDetailDatesChange: (start: Date | null, end: Date | null) => void;
  detailLocation: string;
  onDetailLocationChange: (value: string) => void;
  detailNotes: string;
  onDetailNotesChange: (value: string) => void;
  onSaveDetails: () => void;

  aiPanelOpen: boolean;
  onToggleAiPanel: () => void;
  aiSuggestions: ItinerarySuggestion[];
  aiChat: AiChatMessage[];
  aiChatRef: RefObject<HTMLDivElement | null>;
  aiThinking: boolean;
  aiPromptDraft: string;
  onAiPromptDraftChange: (value: string) => void;
  onSubmitAiPrompt: () => void;
  editItinId: number | null;
  editItinText: string;
  onEditItinTextChange: (value: string) => void;
  onStartEditItin: (id: number, text: string) => void;
  onSaveEditItin: () => void;
  onCancelEditItin: () => void;
  onAcceptSuggestion: (id: number) => void;
  onRejectSuggestion: (id: number) => void;
  onAcceptAllSuggestions: () => void;
}

export function ItineraryTab({
  planTabs,
  eventStart,
  eventEnd,
  itinerary,
  onRemoveItem,
  onAddItem,
  itineraryMode,
  aiLoading,
  itDate,
  itEndDate,
  onItDatesChange,
  itActivity,
  onItActivityChange,
  showDetails,
  onOpenDetails,
  onCloseDetails,
  detailActivity,
  onDetailActivityChange,
  detailDate,
  detailEndDate,
  onDetailDatesChange,
  detailLocation,
  onDetailLocationChange,
  detailNotes,
  onDetailNotesChange,
  onSaveDetails,
  aiPanelOpen,
  onToggleAiPanel,
  aiSuggestions,
  aiChat,
  aiChatRef,
  aiThinking,
  aiPromptDraft,
  onAiPromptDraftChange,
  onSubmitAiPrompt,
  editItinId,
  editItinText,
  onEditItinTextChange,
  onStartEditItin,
  onSaveEditItin,
  onCancelEditItin,
  onAcceptSuggestion,
  onRejectSuggestion,
  onAcceptAllSuggestions,
}: ItineraryTabProps) {
  return (
    <div className={s.fields}>
      {!showDetails && aiLoading && <AiGenerating hint="Drafting your itinerary" lines={0} />}

      <ItineraryDetailsModal
        open={showDetails}
        onClose={onCloseDetails}
        activity={detailActivity}
        onActivityChange={onDetailActivityChange}
        date={detailDate}
        endDate={detailEndDate}
        onDatesChange={onDetailDatesChange}
        location={detailLocation}
        onLocationChange={onDetailLocationChange}
        notes={detailNotes}
        onNotesChange={onDetailNotesChange}
        onSave={onSaveDetails}
        eventStart={eventStart}
        eventEnd={eventEnd}
      />

      {!showDetails && planTabs}

      {!showDetails &&
        itineraryMode === 'ai' &&
        !aiLoading &&
        (aiSuggestions.length > 0 || aiChat.length > 0) && (
          <ItineraryAiPanel
            open={aiPanelOpen}
            onToggle={onToggleAiPanel}
            suggestions={aiSuggestions}
            chat={aiChat}
            chatRef={aiChatRef}
            thinking={aiThinking}
            promptDraft={aiPromptDraft}
            onPromptDraftChange={onAiPromptDraftChange}
            onSubmitPrompt={onSubmitAiPrompt}
            editId={editItinId}
            editText={editItinText}
            onEditTextChange={onEditItinTextChange}
            onStartEdit={onStartEditItin}
            onSaveEdit={onSaveEditItin}
            onCancelEdit={onCancelEditItin}
            onAccept={onAcceptSuggestion}
            onReject={onRejectSuggestion}
            onAcceptAll={onAcceptAllSuggestions}
          />
        )}

      {!showDetails && (
        <div className={s.itineraryInputRow}>
          <DateTimePicker
            // The picker keeps its own open step; a fresh one after each added item
            // comes back folded and empty instead of a stale card.
            key={itinerary.length}
            label="Dates"
            placeholder="Choose dates"
            minDate={eventStart ?? undefined}
            maxDate={eventEnd ?? undefined}
            value={toIsoValue(itDate)}
            endValue={toIsoValue(itEndDate)}
            onRangeChange={(start, end) => onItDatesChange(fromIsoValue(start), fromIsoValue(end))}
          />
          <div className={s.inlineField}>
            <Input
              label="Activity"
              value={itActivity}
              onChange={(e) => onItActivityChange(e.target.value)}
              placeholder="Sunset drinks on the flybridge"
            />
          </div>
        </div>
      )}

      {!showDetails && (
        <button type="button" className={s.addMoreBtn} onClick={onOpenDetails}>
          <span className={s.addMoreBtnIcon}>+</span>
          <span>Add Itinerary Details</span>
        </button>
      )}

      {!showDetails && <AddButton label="Item" onClick={onAddItem} disabled={!itActivity.trim()} />}

      {!showDetails && itinerary.length > 0 && !aiLoading && (
        <div className={s.rowList}>
          <div className={s.listCount}>
            {itinerary.length} item{itinerary.length !== 1 ? 's' : ''} added
          </div>
          {itinerary.map((item) => (
            <div key={item.id} className={s.itineraryRow}>
              <span className={s.itineraryTime}>
                {item.start ? fmtRange(item.start, item.end) : item.time}
              </span>
              <span className={s.itineraryActivity}>{item.activity}</span>
              <button
                type="button"
                className={s.removeBtn}
                onClick={() => onRemoveItem(item.id)}
                aria-label="Remove item"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
